import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useCreateProspectMutation } from '../../store/prospectApi';
import { useGetVerifiedPromotersQuery, PromoterResponse as PromoterResponseType } from '../../store/promoterApi';
import { useGetVendorMeQuery } from '../../store/vendorApi';
import { ChevronLeft, ChevronRight, Check, Mail, User, Building, Phone, Linkedin, Users, Tag, Briefcase } from 'lucide-react';
import { Avatar, PhoneInput, toast, toastMessages } from '@clareefai/ui';
import { Promoter, DEFAULT_INDUSTRY_SECTORS } from '@clareefai/types';
import EditableEmailPreviewCard from '../shared/EditableEmailPreviewCard';
import { EmailTemplateType, useRenderEmailPreviewMutation } from '../../store/emailTemplateApi';

interface ProspectFormProps {
  onClose: () => void;
}

const ProspectForm: React.FC<ProspectFormProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const { isTeamMember } = useAuth();
  const { data: vendor } = useGetVendorMeQuery(undefined, { skip: isTeamMember });
  const { data: promotersData, isLoading: promotersLoading } = useGetVerifiedPromotersQuery();
  const [createProspect, { isLoading, error }] = useCreateProspectMutation();
  const [renderEmailPreview, { data: renderedPreview, isLoading: isRendering }] = useRenderEmailPreviewMutation();
  
  const promoters: Promoter[] = useMemo(() => {
    if (!promotersData) return [];

    return promotersData
      // Only promoters approved for 1:1 reference calls (vendor approved their
      // submission, which sets referenceCallFrequency) can be assigned to prospects.
      .filter((promoter) => promoter.referenceCallFrequency)
      .map((promoter) => ({
      id: promoter.id || '',
      name: `${promoter.firstName} ${promoter.lastName}`,
      email: promoter.email,
      phone: promoter.phone || '',
      company: promoter.company || '',
      avatar: promoter.avatar,
      status: (promoter.status as any) || 'available',
      prospectsReferred: promoter.prospectsReferred || 0,
      conversionRate: promoter.conversionRate || 0,
      totalRewards: promoter.totalRewards || 0,
      joinedDate: promoter.joinedDate || promoter.createdAt || new Date().toISOString(),
    }));
  }, [promotersData]);

  const [currentStep, setCurrentStep] = useState(1);
  const [selectedPromoters, setSelectedPromoters] = useState<string[]>([]);
  const [emailOverride, setEmailOverride] = useState<{ subject: string; body: string } | null>(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    job_title: '',
    email: '',
    phone: '',
    linkedin: '',
    company: '',
    sectors: [] as string[],
    employeeCount: '',
  });

  const sectors = vendor?.preferredIndustries && vendor.preferredIndustries.length > 0
    ? vendor.preferredIndustries
    : DEFAULT_INDUSTRY_SECTORS;

  const employeeCounts = [
    '1-10', '11-50', '51-200', '201-500', '501-1000', '1000+'
  ];

  const handlePromoterToggle = (promoterId: string) => {
    setSelectedPromoters(prev => 
      prev.includes(promoterId) 
        ? prev.filter(id => id !== promoterId)
        : [...prev, promoterId]
    );
  };

  const handleSectorToggle = (sector: string) => {
    setFormData(prev => {
      if (prev.sectors.includes(sector)) {
        // Remove sector if already selected
        return {
          ...prev,
          sectors: prev.sectors.filter(s => s !== sector)
        };
      } else {
        // Add sector only if under limit
        if (prev.sectors.length < 6) {
          return {
            ...prev,
            sectors: [...prev.sectors, sector]
          };
        }
        return prev;
      }
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const triggerRenderPreview = () => {
    renderEmailPreview({
      templateType: EmailTemplateType.PROSPECT_MAGIC_LINK,
      firstName: formData.first_name || 'there',
      email: formData.email || '',
    });
  };

  const handleNext = () => {
    if (currentStep < 4) {
      if (currentStep === 2) {
        triggerRenderPreview();
      }
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const [submittedData, setSubmittedData] = useState<any>(null);

  const handleSubmit = async () => {
    const selectedPromoterNames = promoters
      .filter(p => selectedPromoters.includes(p.id))
      .map(p => p.name)
      .join(', ');

    const payload = {
      first_name: formData.first_name,
      last_name: formData.last_name,
      job_title: formData.job_title.trim() || undefined,
      email: formData.email,
      phone: (formData.phone || '').replace(/\s+/g, ''),
      company: formData.company,
      linkedin: formData.linkedin || undefined,
      promoterId: selectedPromoters[0] || undefined,
      promoterName: selectedPromoterNames || undefined,
      assigned_promoters: selectedPromoters.length > 0 ? selectedPromoters : undefined,
      sectors: formData.sectors.length > 0 ? formData.sectors : undefined,
      companySize: formData.employeeCount || undefined,
      emailSubject: emailOverride?.subject,
      emailBody: emailOverride?.body,
    };

    try {
      setSubmittedData(payload);
      await createProspect(payload).unwrap();
      toast.success(toastMessages.prospect.created);
      setCurrentStep(4);
    } catch (err: any) {
      console.error('Failed to create prospect:', err);
      toast.error(err?.data?.message || toastMessages.generic.error);
    }
  };

  const emailSampleData = {
    firstName: formData.first_name || 'there',
    companyName: vendor?.companyName || '[Company Name]',
    vendorName: vendor ? [vendor.firstName, vendor.lastName].filter(Boolean).join(' ') : '[Vendor Name]',
    invitationReason: 'Discover our solution',
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {[1, 2, 3, 4].map((step) => (
        <React.Fragment key={step}>
          <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
            step <= currentStep 
              ? 'bg-purple-600 border-purple-600 text-white' 
              : 'border-gray-300 text-gray-400'
          }`}>
            {step < currentStep ? <Check className="w-5 h-5" /> : step}
          </div>
          {step < 4 && (
            <div className={`w-16 h-0.5 ${
              step < currentStep ? 'bg-purple-600' : 'bg-gray-300'
            }`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('prospects.form.stepInfo')}</h3>
        <p className="text-gray-600">{t('prospects.form.stepInfoSubtitle')}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <User className="w-4 h-4 inline mr-1" />
            {t('prospects.form.firstName')} *
          </label>
          <div className="space-y-0">
            <input
              type="text"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              className="form-input"
              placeholder="John"
              required
            />
            <div className="h-5 mt-1"></div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <User className="w-4 h-4 inline mr-1" />
            {t('prospects.form.lastName')} *
          </label>
          <div className="space-y-0">
            <input
              type="text"
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              className="form-input"
              placeholder="Doe"
              required
            />
            <div className="h-5 mt-1"></div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Phone className="w-4 h-4 inline mr-1" />
            {t('prospects.form.phone')} *
          </label>
          <div className="space-y-0">
            <PhoneInput
              value={formData.phone}
              onChange={(value) => setFormData({ ...formData, phone: value })}
              placeholder="123 456 7890"
              required
            />
            <div className="h-5 mt-1"></div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Mail className="w-4 h-4 inline mr-1" />
            {t('prospects.form.email')} *
          </label>
          <div className="space-y-0">
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="form-input"
              placeholder="john.doe@company.com"
              required
            />
            <div className="h-5 mt-1"></div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Briefcase className="w-4 h-4 inline mr-1" />
            {t('common.jobTitle')}
          </label>
          <div className="space-y-0">
            <input
              type="text"
              name="job_title"
              value={formData.job_title}
              onChange={handleChange}
              className="form-input"
              placeholder={t('common.jobTitle')}
              maxLength={100}
            />
            <div className="h-5 mt-1"></div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Linkedin className="w-4 h-4 inline mr-1" />
            {t('prospects.form.linkedin')}
          </label>
          <div className="space-y-0">
            <input
              type="url"
              name="linkedin"
              value={formData.linkedin}
              onChange={handleChange}
              className="form-input"
              placeholder="https://linkedin.com/in/johndoe"
            />
            <div className="h-5 mt-1"></div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Building className="w-4 h-4 inline mr-1" />
            {t('prospects.form.company')} *
          </label>
          <div className="space-y-0">
            <input
              type="text"
              name="company"
              value={formData.company}
              onChange={handleChange}
              className="form-input"
              placeholder="Company name"
              required
            />
            <div className="h-5 mt-1"></div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Users className="w-4 h-4 inline mr-1" />
            {t('prospects.form.employees')} *
          </label>
          <div className="space-y-0">
            <select
              name="employeeCount"
              value={formData.employeeCount}
              onChange={handleChange}
              className="form-input"
            >
              <option value="">{t('prospects.form.select')}</option>
              {employeeCounts.map(count => (
                <option key={count} value={count}>{count}</option>
              ))}
            </select>
            <div className="h-5 mt-1"></div>
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          <Tag className="w-4 h-4 inline mr-1" />
          {t('prospects.form.industrySectors')} {formData.sectors.length > 0 && `(${formData.sectors.length}/6)`}
        </label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {sectors.map(sector => {
            const isSelected = formData.sectors.includes(sector);
            const isDisabled = !isSelected && formData.sectors.length >= 6;
            return (
              <button
                key={sector}
                type="button"
                onClick={() => handleSectorToggle(sector)}
                disabled={isDisabled}
                className={`p-3 text-sm rounded-lg border-2 transition-all duration-200 ${
                  isSelected
                    ? 'bg-purple-100 border-purple-400 text-purple-800 shadow-md transform scale-105'
                    : isDisabled
                    ? 'bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed opacity-50'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium">{sector}</span>
                  {isSelected && (
                    <Check className="w-4 h-4 text-purple-600" />
                  )}
                </div>
              </button>
            );
          })}
        </div>
        <div className="h-5 mt-1">
          {formData.sectors.length >= 6 && (
            <p className="text-sm text-gray-500">{t('prospects.form.maxSectors')}</p>
          )}
        </div>
      </div>

    </div>
  );

  const renderStep2 = () => {
    // Parse the legacy frequency keys ('1_week', '2_month', …) into a cap + period
    // and count this period's booked meetings against it.
    const FREQUENCY_CAPS: Record<string, { max: number; period: 'week' | 'month' }> = {
      '1_week': { max: 1, period: 'week' },
      '2_month': { max: 2, period: 'month' },
      '3_month': { max: 3, period: 'month' },
      '1_month': { max: 1, period: 'month' },
    };

    const getFrequencyInfo = (promoterData: PromoterResponseType) => {
      const cap = FREQUENCY_CAPS[promoterData.referenceCallFrequency ?? ''];
      if (!cap) {
        return { current: 0, max: null, isFull: false, display: 'No limit' };
      }

      const { max, period } = cap;

      // Count meetings for the appropriate period
      const now = new Date();
      let periodStart: Date;

      if (period === 'month') {
        periodStart = new Date(now.getFullYear(), now.getMonth(), 1);
      } else {
        const dayOfWeek = now.getDay();
        periodStart = new Date(now);
        periodStart.setDate(now.getDate() - dayOfWeek);
        periodStart.setHours(0, 0, 0, 0);
      }

      const currentMeetings = (promoterData.meetings || []).filter(meeting => {
        if (!meeting?.accepted_date?.date) return false;
        const meetingDate = new Date(meeting.accepted_date.date);
        const status = meeting.status?.toUpperCase();
        return meetingDate >= periodStart &&
               (status === 'CONFIRMED' || status === 'COMPLETED');
      }).length;

      const isFull = currentMeetings >= max;
      const display = `${currentMeetings}/${max}`;

      return { current: currentMeetings, max, isFull, display, period };
    };

    return (
      <div className="space-y-6">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('prospects.form.stepPromoters')}</h3>
          <p className="text-gray-600">{t('prospects.form.stepPromotersSubtitle')}</p>
        </div>

        {promotersLoading ? (
          <div className="text-center py-8">
            <p className="text-gray-600">{t('prospects.form.loadingPromoters')}</p>
          </div>
        ) : promoters.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-600">{t('prospects.form.noPromotersAvailable')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {promoters.map((promoter) => {
              const promoterData = promotersData?.find(p => p.id === promoter.id);
              const frequencyInfo = promoterData ? getFrequencyInfo(promoterData) : { current: 0, max: null, isFull: false, display: 'No limit' };
              const isDisabled = frequencyInfo.isFull;

              return (
                <div
                  key={promoter.id}
                  onClick={() => !isDisabled && handlePromoterToggle(promoter.id)}
                  className={`relative p-3 border-2 rounded-lg transition-all ${
                    isDisabled
                      ? 'border-red-200 bg-red-50 cursor-not-allowed opacity-60'
                      : selectedPromoters.includes(promoter.id)
                      ? 'border-purple-500 bg-purple-50 cursor-pointer'
                      : 'border-gray-200 hover:border-gray-300 cursor-pointer'
                  }`}
                >
                  {selectedPromoters.includes(promoter.id) && !isDisabled && (
                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center shadow-sm">
                      <Check className="w-3.5 h-3.5 text-white" />
                    </div>
                  )}
                  <div className="flex items-center space-x-3">
                    <Avatar
                      src={promoter.avatar}
                      name={promoter.name}
                      size="md"
                      className="flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-900 text-sm truncate">{promoter.name}</h4>
                      <p className="text-xs text-gray-600 truncate">{promoter.company || t('prospects.form.noCompany')}</p>
                      <div className="flex items-center justify-between mt-1 gap-1">
                        <div className="flex items-center space-x-2 flex-shrink-0">
                          <span className="text-xs text-gray-500" title="Assigned Prospects">
                            {promoterData?.assignedProspectsCount ?? 0} {t('prospects.form.assigned')}
                          </span>
                          {frequencyInfo.max !== null && (
                            <span
                              className={`text-xs font-medium ${
                                frequencyInfo.isFull ? 'text-red-600' : 'text-blue-600'
                              }`}
                              title={`Meetings: ${frequencyInfo.current}/${frequencyInfo.max} ${frequencyInfo.period || 'month'}`}
                            >
                              {frequencyInfo.display} /{frequencyInfo.period || 'month'}
                            </span>
                          )}
                        </div>
                        <span className={`text-xs px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                          isDisabled
                            ? 'bg-red-100 text-red-800'
                            : promoter.status === 'available'
                            ? 'bg-green-100 text-green-800'
                            : promoter.status === 'testimonial_done'
                            ? 'bg-purple-100 text-purple-800'
                            : promoter.status === 'review'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {isDisabled
                            ? t('prospects.form.statusFull')
                            : promoter.status === 'available'
                            ? t('prospects.form.statusAvailable')
                            : promoter.status === 'testimonial_done'
                            ? t('prospects.form.statusTestimonialDone')
                            : promoter.status === 'review'
                            ? t('prospects.form.statusReview')
                            : promoter.status === 'testimonial_ready'
                            ? t('prospects.form.statusTestimonialReady')
                            : t('prospects.form.statusInvited')}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {selectedPromoters.length > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <Check className="w-5 h-5 text-green-600" />
              <span className="font-medium text-green-900">
                {selectedPromoters.length > 1
                  ? t('prospects.form.promotersSelected', { count: selectedPromoters.length })
                  : t('prospects.form.promoterSelected', { count: selectedPromoters.length })}
              </span>
            </div>
            <div className="mt-2 text-sm text-green-700">
              {promoters
                .filter(p => selectedPromoters.includes(p.id))
                .map(p => p.name)
                .join(', ')}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('prospects.form.stepEmail')}</h3>
        <p className="text-gray-600">{t('prospects.form.stepEmailSubtitle')}</p>
      </div>

      <div className="flex items-center space-x-3 text-sm text-gray-600 mb-2 px-1">
        <Mail className="w-4 h-4 text-blue-500" />
        <span className="font-medium">{t('prospects.form.to')}:</span>
        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
          {formData.email || t('prospects.form.emailPlaceholder')}
        </span>
      </div>

      <EditableEmailPreviewCard
        templateType={EmailTemplateType.PROSPECT_MAGIC_LINK}
        renderedHtml={renderedPreview?.body}
        renderedSubject={renderedPreview?.subject}
        isRendering={isRendering}
        sampleData={emailSampleData}
        override={emailOverride}
        onOverrideChange={setEmailOverride}
      />

      <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
            <Check className="w-4 h-4 text-white" />
          </div>
          <div>
            <h4 className="font-semibold text-green-900 mb-1">{t('prospects.form.emailReady')}</h4>
            <p className="text-sm text-green-700">
              {t('prospects.form.emailReadyDesc', { name: `${formData.first_name || ''} ${formData.last_name || ''}`.trim() || t('prospects.form.theProspect') })}
            </p>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Mail className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900 mb-1">{t('prospects.form.autoPersonalization')}</h4>
            <p className="text-sm text-blue-700">
              {t('prospects.form.autoPersonalizationDesc')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => {
    const data = submittedData || formData;

    return (
      <div className="text-center space-y-8">
        <div className="w-24 h-24 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto shadow-lg">
          <Check className="w-12 h-12 text-white" />
        </div>
        
        <div>
          <h3 className="text-3xl font-bold text-gray-900 mb-3">{t('prospects.form.success')}</h3>
          <p className="text-xl text-gray-600 mb-2">{t('prospects.form.prospectAddedSuccess')}</p>
          <p className="text-gray-500">{t('prospects.form.invitationSent')}</p>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-200">
          <h4 className="font-semibold text-gray-900 mb-4 flex items-center justify-center">
            <User className="w-5 h-5 mr-2 text-purple-600" />
            {t('prospects.form.prospectSummary')}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="font-medium text-gray-600">{t('prospects.form.nameLabel')}</span>
                <span className="text-gray-900">{`${data.first_name || ''} ${data.last_name || ''}`.trim()}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-gray-600">{t('prospects.form.emailLabel')}</span>
                <span className="text-gray-900">{data.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-gray-600">{t('prospects.form.phoneLabel')}</span>
                <span className="text-gray-900">{data.phone || t('prospects.form.notProvided')}</span>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="font-medium text-gray-600">{t('prospects.form.companyLabel')}</span>
                <span className="text-gray-900">{data.company || t('prospects.form.notProvided')}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-gray-600">{t('prospects.form.promotersLabel')}</span>
                <span className="text-gray-900">{submittedData?.assigned_promoters?.length || selectedPromoters.length || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-gray-600">{t('prospects.form.statusLabel')}</span>
                <span className="status-badge status-invited">{t('prospects.form.toIntroduce')}</span>
              </div>
            </div>
          </div>

          {data.sectors && data.sectors.length > 0 && (
            <div className="mt-4 pt-4 border-t border-purple-200">
              <div className="text-left">
                <span className="font-medium text-gray-600 text-sm">{t('prospects.form.industrySectorsLabel')}</span>
                <div className="flex flex-wrap gap-2 mt-2">
                  {data.sectors.map((sector: string) => (
                    <span
                      key={sector}
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800"
                    >
                      {sector}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-center space-x-2 text-blue-700">
            <Mail className="w-5 h-5" />
            <span className="font-medium">{t('prospects.form.emailSentTo', { email: data.email })}</span>
          </div>
          <p className="text-sm text-blue-600 mt-1">
            {t('prospects.form.appearInColumn')}
          </p>
        </div>
      </div>
    );
  };

  const isStep1Valid = formData.first_name && formData.last_name && formData.email && formData.phone && formData.company && formData.employeeCount;

  return (
    <div className="space-y-8">
      {renderStepIndicator()}

      <div className="min-h-96">
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
        {currentStep === 4 && renderStep4()}
      </div>

      <div className="flex justify-between pt-6 border-t border-gray-200">
        {currentStep !== 4 && (
          <button
            type="button"
            onClick={currentStep === 1 ? onClose : handlePrevious}
            className="flex items-center px-6 py-3 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            {currentStep === 1 ? (
              t('common.cancel')
            ) : (
              <>
                <ChevronLeft className="w-4 h-4 mr-1" />
                {t('common.previous')}
              </>
            )}
          </button>
        )}
        {currentStep === 4 && <div />}

        {currentStep === 1 && (
          <button
            type="button"
            onClick={handleNext}
            disabled={!isStep1Valid}
            className="flex items-center btn-primary disabled:opacity-50 disabled:cursor-not-allowed px-6 py-3"
          >
            {t('common.next')}
            <ChevronRight className="w-4 h-4 ml-1" />
          </button>
        )}

        {currentStep === 2 && (
          <button
            type="button"
            onClick={handleNext}
            disabled={selectedPromoters.length === 0}
            className="flex items-center btn-primary disabled:opacity-50 disabled:cursor-not-allowed px-6 py-3"
          >
            {t('common.next')}
            <ChevronRight className="w-4 h-4 ml-1" />
          </button>
        )}

        {currentStep === 3 && (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isLoading}
            className="flex items-center btn-primary disabled:opacity-50 disabled:cursor-not-allowed px-6 py-3"
          >
            {isLoading ? t('prospects.form.creating') : t('prospects.form.sendEmail')}
            <ChevronRight className="w-4 h-4 ml-1" />
          </button>
        )}

        {currentStep === 4 && (
          <button
            type="button"
            onClick={onClose}
            className="btn-primary px-6 py-3"
          >
            {t('common.close')}
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mt-4">
          {error && 'data' in error && typeof error.data === 'object' && error.data !== null && 'message' in error.data
            ? (error.data as { message: string }).message
            : t('common.somethingWrong')}
        </div>
      )}
    </div>
  );
};

export default ProspectForm;